import React, { useCallback, useEffect, useState } from "react";
import { api, money, fmtDateTime } from "./api";

const toForm = (s) => ({
  tiers: s.tiers.map(([l, p]) => [String(l), String(p)]),
  shipping: String(s.shipping),
  container: String(s.container),
  compost_price: String(s.compost_price),
  commission_fixed: String(s.commission_fixed),
  compost_commission: String(s.compost_commission),
  bulk_pct: String(s.bulk_pct),
  card_number: s.card_number,
  card_bank: s.card_bank,
});

const LABELS = {
  PRICE_TIERS: "Precios del lixiviado",
  SHIPPING_FEE: "Costo de envío",
  CONTAINER_FEE: "Costo por envase",
  COMPOST_PRICE: "Precio de la composta",
  COMMISSION_FIXED: "Comisión bidón",
  COMPOST_COMMISSION: "Comisión composta",
  BULK_COMMISSION_PERCENT: "Comisión pedidos grandes (%)",
  CARD_NUMBER: "Tarjeta de pago",
  CARD_BANK: "Banco",
};

export default function PricingSettings({ notify }) {
  const [saved, setSaved] = useState(null); // lo que tiene el servidor
  const [f, setF] = useState(null);         // lo que se está editando
  const [tell, setTell] = useState(true);
  const [log, setLog] = useState(null);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(
    () => api("/api/admin/settings")
      .then((s) => { setSaved(s); setF(toForm(s)); })
      .catch((e) => notify(e.message, true)),
    [notify]
  );
  const loadLog = useCallback(
    () => api("/api/admin/settings/log").then(setLog).catch(() => {}),
    []
  );
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showLog) loadLog(); }, [showLog, loadLog]);

  if (!f || !saved) return null;

  const field = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setTier = (i, j) => (e) => {
    const tiers = f.tiers.map((r) => [...r]);
    tiers[i][j] = e.target.value;
    setF({ ...f, tiers });
  };
  const addTier = () => setF({ ...f, tiers: [...f.tiers, ["", ""]] });
  const delTier = (i) => setF({ ...f, tiers: f.tiers.filter((_, x) => x !== i) });

  const changes = [];
  if (Number(f.commission_fixed) !== saved.commission_fixed) changes.push(`bidones: ${money(f.commission_fixed)}`);
  if (Number(f.compost_commission) !== saved.compost_commission) changes.push(`composta: ${money(f.compost_commission)}`);
  if (Number(f.bulk_pct) !== saved.bulk_pct) changes.push(`pedidos grandes: ${f.bulk_pct}%`);
  const cardChanged = f.card_number.replace(/\D/g, "") !== saved.card_number.replace(/\D/g, "");

  const save = async () => {
    let msg = "¿Guardar los cambios?";
    if (changes.length || cardChanged) {
      msg = "Se guardarán los cambios.";
      if (changes.length) msg += `\n\nEn las siguientes ventas la comisión será → ${changes.join(" · ")}.${tell ? " Se avisará a los socios." : ""}`;
      if (cardChanged) msg += "\n\nCambiaste la tarjeta de pago: se avisará a todos los socios.";
      msg += "\n\n¿Continuar?";
    }
    if (!window.confirm(msg)) return;
    try {
      const s = await api("/api/admin/settings", {
        method: "PATCH",
        body: {
          tiers: f.tiers.map(([l, p]) => [Number(l), Number(p)]),
          shipping: f.shipping === "" ? 0 : f.shipping,
          container: f.container === "" ? 0 : f.container,
          compost_price: f.compost_price,
          commission_fixed: f.commission_fixed,
          compost_commission: f.compost_commission,
          bulk_pct: f.bulk_pct,
          card_number: f.card_number,
          card_bank: f.card_bank,
          notify: tell,
        },
      });
      setSaved(s); setF(toForm(s));
      if (showLog) loadLog();
      notify("Cambios guardados");
    } catch (e) { notify(e.message, true); }
  };

  return (
    <div className="card">
      <div className="card-head"><h2>Precios, comisiones y tarjeta</h2></div>
      <p className="muted small" style={{ marginBottom: ".75rem" }}>
        Los cambios aplican solo a los pedidos nuevos. Los pedidos que ya existen conservan su precio y comisión.
      </p>

      <h3 style={{ margin: ".5rem 0" }}>Lixiviado (litros → precio total)</h3>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead><tr><th>Litros</th><th>Precio ($)</th><th></th></tr></thead>
          <tbody>
            {f.tiers.map((r, i) => (
              <tr key={i}>
                <td><input type="number" min="1" value={r[0]} onChange={setTier(i, 0)} /></td>
                <td><input type="number" min="1" value={r[1]} onChange={setTier(i, 1)} /></td>
                <td className="right"><button className="btn btn-sm btn-danger-soft" onClick={() => delTier(i)}>Quitar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="actions"><button className="btn btn-sm btn-soft" onClick={addTier}>+ Agregar rango</button></div>
      <p className="muted small">Entre dos rangos el precio por litro se calcula proporcional. Deben ir en orden ascendente.</p>

      <div className="divider" />
      <div className="form-grid">
        <div className="field"><label>Composta (saco de 20 kg): precio por saco ($)</label><input type="number" min="1" value={f.compost_price} onChange={field("compost_price")} /></div>
        <div className="field"><label>Costo de envío ($)</label><input type="number" min="0" value={f.shipping} onChange={field("shipping")} /></div>
        <div className="field"><label>Costo por envase ($)</label><input type="number" min="0" value={f.container} onChange={field("container")} /></div>
      </div>

      <div className="divider" />
      <h3 style={{ margin: ".5rem 0" }}>Comisiones</h3>
      <div className="form-grid">
        <div className="field"><label>Bidón de lixiviado ($ por pedido)</label><input type="number" min="0" value={f.commission_fixed} onChange={field("commission_fixed")} /></div>
        <div className="field"><label>Composta ($ por pedido)</label><input type="number" min="0" value={f.compost_commission} onChange={field("compost_commission")} /></div>
        <div className="field"><label>Pedidos grandes (% que te quedas)</label><input type="number" min="0" max="90" value={f.bulk_pct} onChange={field("bulk_pct")} /></div>
      </div>

      {changes.length > 0 && (
        <div className="alert alert-warn" style={{ marginTop: ".75rem" }}>
          En las siguientes ventas se cobrará → {changes.join(" · ")}
          <label style={{ display: "block", marginTop: ".4rem" }}>
            <input type="checkbox" checked={tell} onChange={(e) => setTell(e.target.checked)} /> Avisar a los socios por notificación
          </label>
        </div>
      )}

      <div className="divider" />
      <h3 style={{ margin: ".5rem 0" }}>Dónde te pagan los socios</h3>
      <div className="form-grid">
        <div className="field"><label>Número de tarjeta (16 dígitos)</label><input inputMode="numeric" value={f.card_number} onChange={field("card_number")} /></div>
        <div className="field"><label>Banco</label><input value={f.card_bank} onChange={field("card_bank")} /></div>
      </div>
      {cardChanged && (
        <div className="alert alert-warn" style={{ marginTop: ".75rem" }}>
          Al guardar, todos los socios recibirán una notificación avisando que cambió la tarjeta.
        </div>
      )}

      <div className="actions">
        <button className="btn" onClick={save}>Guardar cambios</button>
        <button className="btn btn-ghost" onClick={() => setF(toForm(saved))}>Descartar</button>
        <button className="btn btn-soft" onClick={() => setShowLog(!showLog)}>{showLog ? "Ocultar historial" : "Ver historial de cambios"}</button>
      </div>

      {showLog && (
        <div className="tbl-wrap" style={{ marginTop: ".75rem" }}>
          <table className="tbl">
            <thead><tr><th>Fecha</th><th>Quién</th><th>Qué</th><th>Antes</th><th>Después</th></tr></thead>
            <tbody>
              {(log || []).map((l) => (
                <tr key={l.id}>
                  <td className="nowrap">{fmtDateTime(l.created_at)}</td>
                  <td>{l.user_name || "—"}</td>
                  <td>{LABELS[l.k] || l.k}</td>
                  <td className="small" style={{ wordBreak: "break-all" }}>{l.old_v ?? "—"}</td>
                  <td className="small" style={{ wordBreak: "break-all" }}><b>{l.new_v}</b></td>
                </tr>
              ))}
              {log && log.length === 0 && <tr><td colSpan="5" className="muted">Aún no hay cambios.</td></tr>}
              {!log && <tr><td colSpan="5" className="muted">Cargando...</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted small" style={{ marginTop: ".5rem" }}>
        En el historial, los precios del lixiviado se ven como litros:precio separados por coma.
      </p>
    </div>
  );
}